import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'react-router';
import { Star } from 'lucide-react';
import { FeedbackAnswer, FeedbackPageView } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Alert, Button } from '../../components/ui';
import { Unsubscribed, feedbackUrl, getFeedback, problemText } from './api';
import { LinkProblem, PageLoading, PublicShell, Section } from './PublicShell';

function Unsubscribe({ token }: { token: string }) {
  const stop = useMutation({ mutationFn: () => api.post(feedbackUrl(token, '/unsubscribe'), { body: {}, schema: Unsubscribed, quiet401: true }) });
  if (stop.isSuccess) return <Alert kind="success">{t('publicLinks.fb.unsub.done')}</Alert>;
  return (
    <Section title={t('publicLinks.fb.unsub.title')}>
      <p className="text-sm text-slate-700">{t('publicLinks.fb.unsub.body')}</p>
      {stop.isError ? <Alert>{problemText(stop.error)}</Alert> : null}
      <Button variant="primary" disabled={stop.isPending} onClick={() => stop.mutate()}>
        {t('publicLinks.fb.unsub.button')}
      </Button>
    </Section>
  );
}

function RatingForm({ token, view }: { token: string; view: FeedbackPageView }) {
  const qc = useQueryClient();
  const [rating, setRating] = useState<number | null>(view.answer?.rating ?? view.preselected);
  const [comment, setComment] = useState(view.answer?.comment ?? '');
  const [missing, setMissing] = useState(false);
  const send = useMutation({
    mutationFn: (body: FeedbackAnswer) => api.post(feedbackUrl(token), { body, schema: FeedbackPageView, quiet401: true }),
    onSuccess: (v) => qc.setQueryData(['public-link', 'feedback', token], v),
  });
  const locked = view.answer !== null && !view.canChange;

  const submit = () => {
    if (rating === null) return setMissing(true);
    setMissing(false);
    send.mutate({ rating, comment: view.allowComment ? comment : '' });
  };

  return (
    <Section>
      <p className="text-lg font-medium">{view.question}</p>
      {send.isSuccess ? <Alert kind="success">{t('publicLinks.fb.thanks')}</Alert> : null}
      {locked ? <Alert kind="info">{t('publicLinks.fb.locked')}</Alert> : null}
      {view.answer && !locked ? <p className="text-sm text-slate-700">{t('publicLinks.fb.thanksChange')}</p> : null}
      <fieldset disabled={locked}>
        <legend className="mb-2 text-sm font-medium">{t('publicLinks.fb.rating')}</legend>
        <div className="flex justify-between gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <label key={n} className="flex-1">
              <input type="radio" name="rating" className="peer sr-only" checked={rating === n} onChange={() => setRating(n)} aria-label={t('publicLinks.fb.star', { n })} />
              <span className="flex min-h-14 cursor-pointer flex-col items-center justify-center rounded-lg border border-slate-300 bg-white text-xl font-semibold peer-checked:border-brand peer-checked:bg-brand-soft peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand">
                <Star aria-hidden="true" className={`h-5 w-5 ${rating !== null && n <= rating ? 'fill-current text-brand' : 'text-slate-400'}`} />
                {n}
              </span>
            </label>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-xs text-slate-600">
          <span>{t('publicLinks.fb.low')}</span>
          <span>{t('publicLinks.fb.high')}</span>
        </div>
        {missing ? <p className="mt-2 text-sm text-red-700">{t('publicLinks.fb.pick')}</p> : null}
        {view.allowComment ? (
          <div className="mt-4">
            <label htmlFor="fb-comment" className="mb-1 block text-sm font-medium">
              {t('publicLinks.fb.comment')}
            </label>
            <textarea id="fb-comment" rows={4} maxLength={2000} className="block w-full rounded-md border border-slate-300 px-3 py-2 text-base" value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
        ) : null}
      </fieldset>
      {send.isError ? <Alert>{problemText(send.error)}</Alert> : null}
      {!locked ? (
        <Button variant="primary" className="w-full" disabled={send.isPending} onClick={submit}>
          {view.answer ? t('publicLinks.fb.change') : t('publicLinks.fb.send')}
        </Button>
      ) : null}
    </Section>
  );
}

/**
 * /f/:token. Opening the link records nothing (email scanners follow links): the rating from `?r=` is only
 * pre-selected, and the visitor presses Send. `?u=1` is the unsubscribe link at the foot of the email.
 */
export function FeedbackPage() {
  const { token = '' } = useParams();
  const search = new URLSearchParams(useLocation().search);
  const unsubscribe = search.get('u') === '1';
  const r = Number(search.get('r'));
  const preselect = Number.isInteger(r) && r >= 1 && r <= 5 ? r : null;
  const q = useQuery({ queryKey: ['public-link', 'feedback', token], queryFn: () => getFeedback(token, preselect), retry: false });

  return (
    <PublicShell token={token} title={t('publicLinks.fb.title')}>
      {q.isPending ? (
        <PageLoading />
      ) : q.isError ? (
        <LinkProblem error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <p className="text-slate-700">{t('publicLinks.fb.at', { site: q.data.siteName })}</p>
          {unsubscribe ? <Unsubscribe token={token} /> : <RatingForm token={token} view={q.data} />}
        </>
      )}
    </PublicShell>
  );
}
